import { useEffect, useState } from 'react';
import { callAPI } from '../utils/apiUtils';
import { apiUrls } from '../utils/apiUrls';

const COMPACT = 'compact_product_cards';
const HIDE_ICONS = 'hide_allergen_icons';

const readStored = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const store = (key, on) => {
  try {
    window.localStorage.setItem(key, on ? '1' : '0');
  } catch {
    // storage blocked: the answer still applies to this visit
  }
};

/**
 * The two POS settings that change how the menu's product cards are drawn (Settings page,
 * ConfigsController::POS_SETTINGS), read with the one request the Settings page itself uses:
 *  - compactCards ("Compact product cards"): on by default, switched off per restaurant.
 *  - hideAllergenIcons ("Hide allergen icons"): off by default, so the icons show as they always have.
 * Each is a restaurant-wide switch kept on the server; the last answer is kept on this device so the
 * menu is drawn the right way straight away on the next visit. Until a device has heard otherwise it
 * draws compact cards and shows the icons. When the request fails the stored answers stay; when the
 * API answers without a setting (an API that does not have it yet) that setting is treated as off:
 * compact cards would have no switch to turn them off again, and hidden icons no way to bring them back.
 */
export default function useMenuCardSettings() {
  const [compactCards, setCompactCards] = useState(() => readStored(COMPACT) !== '0');
  const [hideAllergenIcons, setHideAllergenIcons] = useState(() => readStored(HIDE_ICONS) === '1');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await callAPI(apiUrls.POS_SETTINGS_GET, {}, 'GET', null, {}, {}, false);
        const list = res?.data?.data;
        if (!Array.isArray(list) || cancelled) return;
        const apply = (key, set) => {
          const setting = list.find((s) => s.key === key);
          if (!setting) {
            set(false);
            return;
          }
          store(key, !!setting.value);
          set(!!setting.value);
        };
        apply(COMPACT, setCompactCards);
        apply(HIDE_ICONS, setHideAllergenIcons);
      } catch {
        // keep what the device already has
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { compactCards, hideAllergenIcons };
}
